import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { AsolviDatePicker } from "components/AsolviDatePicker";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import Transition from "components/Transition";
import { MachineType, Maybe, UpdateMachineInputType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { setOpen } from "store/slices/dialog.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

type EditMachineInstallDateDialogProps = {
  machine: Maybe<MachineType>;
  loading: boolean;
  externalId: string;
  updateMachine: (
    machine: UpdateMachineInputType
  ) => Promise<{ success: boolean; queued: boolean; abort: boolean }>;
};

export const EditMachineInstallDateDialog: FC<EditMachineInstallDateDialogProps> = (props) => {
  const { machine, loading, externalId, updateMachine } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.dialog.editMachineInstallDate);

  const [newInstallDate, setNewInstallDate] = useState<Maybe<string>>(machine?.installDate);
  const machineInstallDate = machine?.installDate;

  const handleClose = useCallback(() => {
    dispatch(setOpen({ dialogName: "editMachineInstallDate", open: false }));
  }, [dispatch]);

  const updateMachineCb = useCallback(
    async (machine: UpdateMachineInputType) => {
      const ret = await updateMachine(machine);
      if (ret.success) {
        handleClose();
        if (ret.queued) {
          dispatch(addSnackbarMessage({ key: "UpdateMachine-stored" }));
        } else {
          dispatch(addSnackbarMessage({ key: "UpdateMachine-success" }));
        }
      } else if (!ret.success && !ret.abort) {
        dispatch(addSnackbarMessage({ key: "UpdateMachine-fail" }));
      }
    },
    [dispatch, handleClose, updateMachine]
  );

  if (!machine) {
    handleClose();
    return null;
  }
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="EditMachineInstallDateDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="EditMachineInstallDateDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            {`${externalId} `} / <FormattedMessage id="visit.editMachineInstallDate" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        <CardHeader
          sx={{
            fontSize: "1.2rem",
          }}
          subheader={
            <>
              <Typography fontWeight={700}>{machine.name}</Typography>
              <Typography>{machine.machineNo}</Typography>
              {machine.idNo && <Typography>{machine.idNo}</Typography>}
              {machine.location && <Typography>{machine.location}</Typography>}
              {machineInstallDate && (
                <Typography>
                  {`${intl.formatMessage({ id: "general.installed" })} `}
                  <FormattedDate
                    value={machineInstallDate}
                    day="numeric"
                    month="long"
                    year="numeric"
                  />
                </Typography>
              )}
            </>
          }
        />
        <CardContent sx={{ paddingTop: 0 }}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <AsolviDatePicker
                onChange={(value) => {
                  setNewInstallDate(value?.toISOString());
                }}
                label={intl.formatMessage({ id: "equipmentEdit.newInstallDate" })}
                value={newInstallDate ? new Date(newInstallDate) : null}
              />
            </Grid>
            <Grid item>
              <PrimaryButton
                key={`${!newInstallDate || loading}`}
                disabled={!newInstallDate || loading}
                fullWidth
                data-testid="EditMachineInstallDateDialog-SaveButton"
                onClick={() => {
                  updateMachineCb({
                    id: machine.id || "",
                    idNo: machine.idNo,
                    machineNo: machine.machineNo,
                    modelGuid: machine.modelGuid!,
                    addressRowNo: machine.addressRowNo,
                    location: machine.location,
                    information: machine.details?.information ?? "",
                    installDate: newInstallDate,
                  });
                }}
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Dialog>
  );
};
